import React, { useEffect, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Stack,
} from "@mui/material";

import { Formik } from "formik";

import { Property } from "./Fields/Property";
import { Vendor } from "./Fields/Vendor";
import { Contract } from "./Fields/Contract";
import InvoiceNumber from "./Fields/InvoiceNumber";
import InvoiceDate from "./Fields/InvoiceDate";
import InvoiceDueDate from "./Fields/InvoiceDueDate";
import InvoiceAmount from "./Fields/InvoiceAmount";
import TransactionDetails from "./TransactionDetails/TransactionDetails";
import moment from "moment";
import currency from "currency.js";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { usePages } from "../../Contexts/InvoicePages";
import logo from "../../../images/logo_white.png";

import * as yup from "yup";
import confirm from "../../Confirm.tsx";
import { CONSUMER_URL } from "../../../helpers/variables.js";

const DATE_FORMAT = "MM/DD/YYYY";

export const validationSchema = yup.object().shape({
  property: yup.object().required("Corporation ID is required"),
  vendor: yup.object().required("Vendor ID is required"),
  invoice_date: yup
    .string()
    .test("is-valid-date", "Bill Date must be in MM/DD/YYYY format", (value) =>
      moment(value, DATE_FORMAT, true).isValid()
    )
    .required("Bill Date is required"),
  invoice_number: yup.string().required("Invoice Number is required"),
  payment_due_date: yup
    .string()
    .test("is-valid-date", "Due Date must be in MM/DD/YYYY format", (value) =>
      moment(value, DATE_FORMAT, true).isValid()
    )
    .test(
      "is-after-or-equal",
      "Due Date must be on or after Bill Date",
      function (value) {
        const { invoice_date } = this.parent;
        if (!moment(invoice_date, DATE_FORMAT, true).isValid()) return false;
        return moment(value, DATE_FORMAT).isSameOrAfter(
          moment(invoice_date, DATE_FORMAT)
        );
      }
    )
    .notRequired(),
  invoice_amount: yup
    .number()
    .typeError("Amount must be a valid number or currency")
    .required("Amount is required"),
  line_items: yup
    .array()
    .of(
      yup.object().shape({
        purpose: yup.string().required("Purpose is required"),
        amount: yup
          .number()
          .typeError("Amount must be a valid number")
          .required("Amount is required"),
        description: yup.string().notRequired(),
      })
    )
    .required("Transaction details are required"),
  contract: yup.object().notRequired(),
});

const FormPanel = ({ bill, file, email, invoice, close }) => {
  const { bill_status } = bill;
  const { openForm, setOpenForm, fileLoadingFailed, pages, setPages } =
    usePages();

  const {
    checked_pages,
    blocked_pages,
    split_pages,
    invoice_pages,
    no_of_pages,
  } = pages;

  /**
   *
   * @param {import("formik").FormikValues} values
   * @param {import("formik").FormikHelpers} formikHelpers
   */
  async function submitForm(values, formikHelpers) {
    values["invoice_amount"] = currency(values["invoice_amount"]).value;

    let total = 0;
    values["line_items"].forEach((i) => {
      i["amount"] = currency(i["amount"]).value;
      total += i["amount"];
    });

    if (values["invoice_amount"] !== currency(total).value) {
      enqueueSnackbar("Total amount must match the specified amount", {
        variant: "warning",
      });
      return;
    }

    values["invoice_type"] = invoice["invoice_type"] || "regular";

    let res = await fetch(
      `${CONSUMER_URL}/send_to_nimble?generate_new_file=${!_.isEqual(
        _.sortBy(checked_pages),
        _.sortBy(no_of_pages)
      )}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bill: { ...bill, profit_center_id: email["profit_center_id"] },
          invoice: values,
          pages: checked_pages,
        }),
      }
    );

    if (res.status === 201) {
      // enqueueSnackbar("Posted Successfully", { variant: "success" });
      // setOpenForm(false);
      // setPages((v) => ({
      //   ...v,
      //   blocked_pages: [...v["blocked_pages"], checked_pages],
      //   checked_pages: [],
      // }));
      close();
      return;
    }

    let data = await res.json();
    enqueueSnackbar(data["error"], { variant: "error" });
    return;
  }

  const [loading, setLoading] = useState(false);

  async function addToQueue() {
    let userConfirmation = await confirm({
      message: `Are you sure you want to add the following pages to the queue: ${_.sortBy(
        checked_pages
      ).join(", ")}?`,
    });
    if (!userConfirmation) return;

    setLoading(true);

    let all_pages = _.union(
      checked_pages,
      blocked_pages,
      split_pages,
      invoice_pages
    );
    let remove_bill = _.isEqual(_.sortBy(all_pages), _.sortBy(no_of_pages));

    let res = await fetch(
      `${CONSUMER_URL}/add_to_queue?file_table_id=${file["_id"]}&bill_id=${bill["_id"]}&remove_bill=${remove_bill}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          selected_pages: checked_pages,
          bill_pages: bill["page_numbers"],
        }),
      }
    );

    if (res.status !== 204) {
      enqueueSnackbar(await res.text(), { variant: "error" });
      setLoading(false);
      return;
    }

    if (remove_bill) {
      close();
    } else {
      setPages((prev) => ({
        ...prev,
        blocked_pages: [...prev.blocked_pages, ...checked_pages],
        checked_pages: [],
      }));
    }

    setLoading(false);
  }

  async function notABill() {
    let userConfirmation = await confirm({
      message: "Are you sure you want to delete this bill?",
    });
    if (!userConfirmation) return;

    setLoading(true);

    let res = await fetch(
      `${CONSUMER_URL}/not_a_bill?file_table_id=${file["_id"]}&bill_id=${bill["_id"]}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          selected_pages: checked_pages,
          bill_pages: bill["page_numbers"],
          no_of_pages,
        }),
      }
    );

    if (res.status !== 204) {
      enqueueSnackbar(await res.text(), { variant: "error" });
      setLoading(false);
      return;
    }

    let all_pages = _.union(
      checked_pages,
      blocked_pages,
      split_pages,
      invoice_pages
    );

    let remove_bill = _.isEqual(_.sortBy(all_pages), _.sortBy(no_of_pages));

    if (remove_bill) {
      close();
    } else {
      setPages((prev) => ({
        ...prev,
        blocked_pages: [...prev.blocked_pages, ...checked_pages],
        checked_pages: [],
      }));
    }

    setLoading(false);
  }

  // /**
  //  * @type {import('react').Ref<import('formik').FormikProps<any>>}
  //  */
  // const formikRef = useRef(null);

  return !openForm && bill_status !== 1 ? (
    <Stack
      height={"100%"}
      justifyContent={"center"}
      alignItems={"center"}
      gap={3}
    >
      <Box
        sx={{ opacity: 0.4, userSelect: "none" }}
        onDragStart={(e) => e.preventDefault()}
        component={"img"}
        src={logo}
        width={"40%"}
      />

      <ButtonGroup disabled={checked_pages.length === 0 && !fileLoadingFailed}>
        <Button onClick={() => setOpenForm(true)}>Open</Button>
        {bill["bill_status"] === -4 && (
          <Button onClick={addToQueue}>Add to queue</Button>
        )}
        <Button onClick={notABill}>Not a bill</Button>
      </ButtonGroup>

      <Backdrop
        open={loading}
        sx={(theme) => ({
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        })}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Stack>
  ) : (
    <Formik
      initialValues={{
        invoice_number: invoice?.invoice_number || "",
        invoice_amount: invoice?.invoice_amount,
        line_items: invoice?.line_items || [],
        invoice_date: invoice?.invoice_date || "",
        payment_due_date: invoice?.payment_due_date || "",

        property: null,
        vendor: null,
        contract: null,
      }}
      initialTouched={{
        invoice_number: true,
        invoice_amount: true,
        line_items: true,
        invoice_date: true,
        payment_due_date: true,
        property: true,
        vendor: true,
        contract: true,
      }}
      onSubmit={submitForm}
      validationSchema={validationSchema}
      validateOnMount
      validateOnBlur
      validateOnChange
    >
      {({ handleSubmit, isSubmitting, errors, validateForm }) => {
        return (
          <>
            <Stack
              component={"form"}
              onSubmit={handleSubmit}
              gap={2}
              overflow={"auto"}
              height={"100%"}
              zIndex={100}
              border={1}
              borderColor={"divider"}
            >
              <Stack p={1.5} flexGrow={1} gap={1.5} width={"100%"}>
                <Box
                  display={"grid"}
                  gridTemplateColumns="repeat(2, 1fr)"
                  gap={1.5}
                >
                  <Property />
                  <Vendor invoice={invoice} />
                  <Contract invoice={invoice} />
                </Box>

                <Box
                  display={"grid"}
                  gridTemplateColumns="repeat(2, 1fr)"
                  gap={1.5}
                >
                  <InvoiceNumber />
                  <InvoiceAmount />
                  <InvoiceDate />
                  <InvoiceDueDate />
                </Box>
                <TransactionDetails />
              </Stack>

              <Stack
                position={"sticky"}
                bottom={0}
                direction={"row"}
                bgcolor={"white"}
                zIndex={200}
                p={1}
                justifyContent={"center"}
                width={"100%"}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={bill_status === 1}
                >
                  Submit
                </Button>
              </Stack>
            </Stack>
            <Backdrop
              open={isSubmitting}
              sx={(theme) => ({
                color: "#fff",
                zIndex: (theme) => theme.zIndex.drawer + 1,
              })}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          </>
        );
      }}
    </Formik>
  );
};

export default FormPanel;
